import { apiFetch, apiFetchText, qs } from './client'
import type {
  AdminCall,
  AdminEvent,
  AdminUser,
  CacheKey,
  CacheValue,
  DebugCollection,
  Enterprise,
  EnterpriseDetail,
  EnterpriseInput,
  EnterpriseUsage,
  MongoQuery,
  MongoResult,
  LlmProvider,
  LlmResult,
  CreateTestCallRequest,
  TranslateResult,
  TtsProvider,
  TtsResult,
  LogLine,
  ScheduledCall,
  Stats,
  Template,
  TemplateCategory,
  UserRole,
} from './types'

export interface UserFilters {
  search?: string
  country?: string
  role?: UserRole | ''
  limit?: number
  skip?: number
  sort?: string
}

export interface CallFilters {
  uid?: string
  search?: string
  from?: string
  to?: string
  status?: string
  source?: string
  provider?: string
  country?: string
  direction?: string
  /**
   * Scopes the list to one enterprise. Served by a different route, which only
   * understands status/from/to/search — the other filters are dropped.
   */
  enterpriseId?: string
  limit?: number
  skip?: number
}

export interface EnterpriseFilters {
  search?: string
  limit?: number
  skip?: number
}

export interface EventFilters {
  uid?: string
  callSid?: string
  type?: string
  action?: string
  search?: string
  from?: string
  to?: string
  limit?: number
  skip?: number
}

export const adminApi = {
  // Identity — the admin gate. 403 means signed in but not an admin.
  me: () =>
    apiFetch<{ user: { uid: string; email?: string; name?: string; picture?: string; role: UserRole } }>(
      '/api/admin/me',
    ).then((r) => r.user),

  // Users
  listUsers: (filters: UserFilters) =>
    apiFetch<{ users: AdminUser[]; total: number }>(`/api/admin/users${qs({ ...filters })}`),
  getUser: (uid: string, calls = 25) =>
    apiFetch<{ user: AdminUser }>(`/api/admin/user/${encodeURIComponent(uid)}${qs({ calls })}`).then(
      (r) => r.user,
    ),
  setRole: (uid: string, role: UserRole) =>
    apiFetch<{ user: AdminUser }>(`/api/admin/user/${encodeURIComponent(uid)}/role`, {
      method: 'POST',
      body: { role },
    }).then((r) => r.user),
  adjustCredits: (uid: string, credits: number, reason?: string) =>
    apiFetch<{ user: AdminUser }>(`/api/admin/user/${encodeURIComponent(uid)}/credits`, {
      method: 'POST',
      body: { credits, reason },
    }).then((r) => r.user),
  deleteUser: (uid: string) =>
    apiFetch<{ deleted: boolean }>(`/api/admin/delete/user/${encodeURIComponent(uid)}`, {
      method: 'POST',
    }),
  resetContacts: (uid: string) =>
    apiFetch<{ success: boolean }>(`/api/admin/reset/contacts/${encodeURIComponent(uid)}`, {
      method: 'POST',
    }),
  resetCalendar: (uid: string) =>
    apiFetch<{ success: boolean }>(`/api/admin/reset/calendar/${encodeURIComponent(uid)}`, {
      method: 'POST',
    }),
  addNumber: (uid: string) =>
    apiFetch<{ user: AdminUser }>(`/api/admin/number/add/${encodeURIComponent(uid)}`, {
      method: 'POST',
    }).then((r) => r.user),
  deleteNumber: (uid: string, number: string) =>
    apiFetch<{ deleted: boolean }>('/api/admin/delete/number', {
      method: 'POST',
      body: { uid, number },
    }),
  deleteDevice: (uid: string, deviceId: string) =>
    apiFetch<{ user: AdminUser }>(
      `/api/admin/delete/user/${encodeURIComponent(uid)}/device/${encodeURIComponent(deviceId)}`,
      { method: 'POST' },
    ).then((r) => r.user),

  // Calls
  listCalls: ({ enterpriseId, ...filters }: CallFilters) =>
    enterpriseId
      ? apiFetch<{ calls: AdminCall[]; total: number }>(
          `/api/admin/enterprise/${encodeURIComponent(enterpriseId)}/calls${qs({
            status: filters.status,
            from: filters.from,
            to: filters.to,
            search: filters.search,
            limit: filters.limit,
            skip: filters.skip,
          })}`,
        )
      : apiFetch<{ calls: AdminCall[]; total: number }>(`/api/admin/calls${qs({ ...filters })}`),
  getCall: (callSid: string) =>
    apiFetch<{ call: AdminCall }>(`/api/admin/call/${encodeURIComponent(callSid)}`).then(
      (r) => r.call,
    ),
  liveCalls: () =>
    apiFetch<{ calls: AdminCall[]; total: number }>('/api/admin/calls/live').then((r) => ({
      calls: r.calls ?? [],
      total: r.total ?? 0,
    })),
  scheduledCalls: (filters: { uid?: string; status?: string; limit?: number; skip?: number }) =>
    apiFetch<{ calls: ScheduledCall[]; total: number }>(
      `/api/admin/calls/scheduled${qs({ ...filters })}`,
    ),
  hangupCall: (callSid: string) =>
    apiFetch<{ success: boolean }>(`/api/admin/call/${encodeURIComponent(callSid)}/hangup`, {
      method: 'POST',
    }),

  // Enterprises — voyp-nodejs/src/enterprise-admin.ts. Members and API keys are
  // managed by the enterprise itself; the admin routes only read them.
  listEnterprises: (filters: EnterpriseFilters) =>
    apiFetch<{ enterprises: Enterprise[]; total: number }>(
      `/api/admin/enterprises${qs({ ...filters })}`,
    ),
  getEnterprise: (id: string) =>
    apiFetch<EnterpriseDetail>(`/api/admin/enterprise/${encodeURIComponent(id)}`).then((r) => ({
      ...r,
      members: r.members ?? [],
      keys: r.keys ?? [],
      usage: r.usage ?? [],
    })),
  createEnterprise: (input: EnterpriseInput) =>
    apiFetch<{ enterprise: Enterprise }>('/api/admin/enterprises', {
      method: 'POST',
      body: input,
    }).then((r) => r.enterprise),
  updateEnterprise: (id: string, input: EnterpriseInput) =>
    apiFetch<{ enterprise: Enterprise }>(`/api/admin/enterprise/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: input,
    }).then((r) => r.enterprise),
  /**
   * The previous owner is demoted to admin, not removed. `createOwner` makes an
   * account for the email if it has none.
   */
  setEnterpriseOwner: (id: string, email: string, createOwner = false) =>
    apiFetch<{ success: boolean }>(`/api/admin/enterprise/${encodeURIComponent(id)}/owner`, {
      method: 'POST',
      body: createOwner ? { email, createOwner } : { email },
    }),
  /** Also revokes every API key — re-enabling does not bring them back. */
  disableEnterprise: (id: string) =>
    apiFetch<{ success: boolean; revokedKeys: number }>(
      `/api/admin/enterprise/${encodeURIComponent(id)}/disable`,
      { method: 'POST' },
    ),
  /**
   * One call by its public `call_…` id or provider callSid. `source` is
   * `call_details` while the call is live (or orphaned), `calls` once it ended.
   */
  findEnterpriseCall: (id: string, callId: string) =>
    apiFetch<{ call: AdminCall; source: 'calls' | 'call_details' }>(
      `/api/admin/enterprise/${encodeURIComponent(id)}/call/${encodeURIComponent(callId)}`,
    ),
  enterpriseUsage: (id: string, month?: string) =>
    apiFetch<{ usage: EnterpriseUsage[] }>(
      `/api/admin/enterprise/${encodeURIComponent(id)}/usage${qs({ month })}`,
    ).then((r) => r.usage ?? []),

  // Logs & events
  listEvents: (filters: EventFilters) =>
    apiFetch<{ events: AdminEvent[]; total: number }>(`/api/admin/events${qs({ ...filters })}`),
  eventTypes: () =>
    apiFetch<{ types: string[] }>('/api/admin/events/types').then((r) => r.types ?? []),
  /** New Relic application logs — free-text substring match, 90 days, max 100. */
  searchLogs: (search: string) =>
    apiFetch<{ logs: LogLine[] }>(`/api/admin/logs${qs({ search })}`, { method: 'POST' }).then(
      (r) => r.logs ?? [],
    ),

  // Dashboard
  stats: (params: { from?: string; to?: string; interval?: string }) =>
    apiFetch<Stats>(`/api/admin/stats${qs({ ...params })}`),

  // Templates
  getTemplates: () =>
    apiFetch<{ templates: Template[]; categories: TemplateCategory[] }>('/api/admin/templates'),
  addTemplate: (template: Template) =>
    apiFetch<unknown>('/api/admin/templates', { method: 'POST', body: template }),
  updateTemplate: (template: Template) =>
    apiFetch<unknown>('/api/admin/templates/update', { method: 'POST', body: template }),
  deleteTemplate: (id: string) =>
    apiFetch<unknown>(`/api/admin/templates/delete/${encodeURIComponent(id)}`, { method: 'POST' }),
  addCategory: (category: TemplateCategory) =>
    apiFetch<unknown>('/api/admin/templates/categories', { method: 'POST', body: category }),
  updateCategory: (category: TemplateCategory) =>
    apiFetch<unknown>('/api/admin/templates/category/update', { method: 'POST', body: category }),
  deleteCategory: (id: string) =>
    apiFetch<unknown>(`/api/admin/templates/categories/delete/${encodeURIComponent(id)}`, {
      method: 'POST',
    }),

  // Push topics
  subscribeTopic: (topic: string, token: string) =>
    apiFetch<unknown>(`/api/admin/subscribe/${encodeURIComponent(topic)}`, {
      method: 'POST',
      body: { token },
    }),
  unsubscribeTopic: (topic: string, token: string) =>
    apiFetch<unknown>(`/api/admin/unsubscribe/${encodeURIComponent(topic)}`, {
      method: 'POST',
      body: { token },
    }),

  // Mailing list CSV
  mailingList: () => apiFetchText('/api/admin/mailing'),
}

/**
 * Debug tools. Every route is superadmin-only and answers 403 for a plain
 * admin. There is deliberately no token endpoint — tokens are decoded in the
 * browser and never sent anywhere.
 */
export const debugApi = {
  collections: () =>
    apiFetch<{ collections: DebugCollection[] }>('/api/admin/debug/mongo/collections').then(
      (r) => r.collections ?? [],
    ),
  find: (query: MongoQuery) =>
    apiFetch<MongoResult>('/api/admin/debug/mongo/find', { method: 'POST', body: query }),
  update: (collection: string, id: unknown, set: string) =>
    apiFetch<{ doc: Record<string, unknown>; invalidated: string[] }>(
      '/api/admin/debug/mongo/update',
      { method: 'POST', body: { collection, id, set } },
    ),

  ttsProviders: () =>
    apiFetch<{ providers: TtsProvider[] }>('/api/admin/debug/tts/providers').then(
      (r) => r.providers ?? [],
    ),
  ttsSay: (body: {
    engine: string
    text: string
    language: string
    voice: string
    format?: string
    sampleRate?: number
  }) => apiFetch<TtsResult>('/api/admin/debug/tts/say', { method: 'POST', body }),

  llmProviders: () =>
    apiFetch<{ providers: LlmProvider[]; default: string | null }>(
      '/api/admin/debug/llm/providers',
    ),
  llmComplete: (body: {
    engine: string
    messages: { role: string; content: string }[]
    advanced?: boolean
    json?: boolean
  }) => apiFetch<LlmResult>('/api/admin/debug/llm/complete', { method: 'POST', body }),

  createCall: (body: CreateTestCallRequest) =>
    apiFetch<{ callSid: string }>('/api/admin/debug/call/create', { method: 'POST', body }),
  callDetails: (callSid: string) =>
    apiFetch<{ details: AdminCall }>(
      `/api/admin/debug/call/${encodeURIComponent(callSid)}`,
    ).then((r) => r.details),
  patchCall: (callSid: string, patch: { autoReply?: boolean; stream?: boolean }) =>
    apiFetch<{ details: AdminCall }>(
      `/api/admin/debug/call/${encodeURIComponent(callSid)}/details`,
      { method: 'POST', body: patch },
    ).then((r) => r.details),

  translate: (body: { text: string; to: string; from?: string | null }) =>
    apiFetch<TranslateResult>('/api/admin/debug/translate', { method: 'POST', body }),

  scanCache: (pattern: string, cursor = 0, count = 100) =>
    apiFetch<{ keys: CacheKey[]; cursor: number; pattern: string }>(
      `/api/admin/debug/cache/scan${qs({ pattern, cursor, count })}`,
    ),
  cacheValue: (key: string) =>
    apiFetch<CacheValue>(`/api/admin/debug/cache/value${qs({ key })}`),
  deleteKeys: (body: { keys?: string[]; pattern?: string; includeProtected?: boolean }) =>
    apiFetch<{ deleted: number; skipped: number }>('/api/admin/debug/cache/delete', {
      method: 'POST',
      body,
    }),
  flushCache: (includeProtected: boolean) =>
    apiFetch<{ flushed: boolean; deleted: number | null; skipped: number }>(
      '/api/admin/debug/cache/flush',
      { method: 'POST', body: { confirm: 'FLUSH', includeProtected } },
    ),
}
